/**
 * DataPill Component
 *
 * A reusable monochrome pill component for displaying data counts.
 * Used for completion states, summaries, and anywhere count + label pairs
 * need to be displayed in a compact, visually consistent format.
 *
 * Design:
 * - Light gray background (Polaris surface-secondary)
 * - Subdued text color for labels
 * - Emphasized count value
 * - Compact pill shape with consistent padding
 */

import { Text } from '@shopify/polaris';

export interface DataPillProps {
  /** The numeric or string value to display prominently */
  value: number | string;
  /** Label describing the value (e.g., "products", "alerts") */
  label: string;
  /** Optional additional CSS class */
  className?: string;
}

/**
 * Renders a monochrome pill with a value and label.
 *
 * @example
 * <DataPill value={42} label="products" />
 * // Renders: [42 products] in a light gray pill
 *
 * @example
 * <DataPill value="150" label="velocity records" />
 * // Renders: [150 velocity records] in a light gray pill
 */
export function DataPill({ value, label, className = '' }: DataPillProps) {
  return (
    <span className={`data-pill ${className}`.trim()}>
      <Text as="span" variant="bodySm" fontWeight="semibold">
        {value}
      </Text>
      <Text as="span" variant="bodySm" tone="subdued">
        {' '}{label}
      </Text>
    </span>
  );
}
